import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { ClipLoader } from "react-spinners";

import { cn } from "../../lib/utils";
import { Icon, type IconName } from "../../lib/icons";

const iconButtonVariants = cva(
  "inline-flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none p-0 border-0 bg-transparent",
  {
    variants: {
      color: {
        primary: "",
        positive: "",
        negative: "",
        notice: "",
        info: "",
        neutral: "",
        white: "",
      },
      size: {
        xsmall: "",
        small: "",
        medium: "",
        large: "",
      },
      isLoading: {
        true: "cursor-not-allowed",
        false: "",
      },
      isDisabled: {
        true: "cursor-not-allowed",
        false: "cursor-pointer",
      },
    },
    defaultVariants: {
      color: "primary",
      size: "medium",
      isLoading: false,
    },
  }
);

export interface IconButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color"> {
  color?:
    | "primary"
    | "positive"
    | "negative"
    | "notice"
    | "info"
    | "neutral"
    | "white";
  size?: "xsmall" | "small" | "medium" | "large";
  isLoading?: boolean;
  isDisabled?: boolean;
  asChild?: boolean;
  /** Icon name from the icon registry */
  icon?: IconName;
  /** Custom icon element (ReactNode) - alternative to icon prop */
  iconElement?: React.ReactNode;
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      className,
      color = "primary",
      size = "medium",
      isLoading,
      asChild = false,
      icon,
      iconElement,
      disabled,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";

    const isDisabled = disabled || isLoading || false;

    const getLoaderColor = () => {
      if (color === "white") {
        return "var(--color-surface-ink-neutral-normal)";
      }
      return `var(--color-action-ink-${color}-normal)`;
    };

    const getIconSize = () => {
      switch (size) {
        case "xsmall":
          return 16;
        case "small":
          return 18;
        case "medium":
          return 20;
        case "large":
          return 24;
        default:
          return 20;
      }
    };

    const getIconColorClass = () => {
      // Color classes with normal, hover, focus, and disabled states
      const colorMap: Record<string, string> = {
        primary:
          "text-action-ink-primary-normal hover:text-action-ink-primary-subtle focus-visible:text-action-ink-primary-subtle disabled:text-action-ink-primary-muted",
        positive:
          "text-action-ink-positive-normal hover:text-action-ink-positive-subtle focus-visible:text-action-ink-positive-subtle disabled:text-action-ink-positive-muted",
        negative:
          "text-action-ink-negative-normal hover:text-action-ink-negative-subtle focus-visible:text-action-ink-negative-subtle disabled:text-action-ink-negative-muted",
        notice:
          "text-action-ink-notice-normal hover:text-action-ink-notice-subtle focus-visible:text-action-ink-notice-subtle disabled:text-action-ink-notice-muted",
        info: "text-action-ink-info-normal hover:text-action-ink-info-subtle focus-visible:text-action-ink-info-subtle disabled:text-action-ink-info-muted",
        neutral:
          "text-action-ink-neutral-normal hover:text-action-ink-neutral-subtle focus-visible:text-action-ink-neutral-subtle disabled:text-action-ink-neutral-muted",
        white:
          "text-surface-ink-white-subtle hover:text-surface-ink-white-normal focus-visible:text-surface-ink-white-normal disabled:text-surface-ink-white-muted",
      };
      return colorMap[color] || colorMap.primary;
    };

    const buttonContent = (
      <>
        {isLoading ? (
          <ClipLoader color={getLoaderColor()} size={getIconSize()} />
        ) : icon ? (
          <Icon
            name={icon}
            size={getIconSize()}
            className={getIconColorClass()}
          />
        ) : iconElement ? (
          <span className={getIconColorClass()}>{iconElement}</span>
        ) : null}
      </>
    );

    return (
      <Comp
        className={cn(
          iconButtonVariants({
            color,
            size,
            isLoading,
            isDisabled,
          }),
          className
        )}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {buttonContent}
      </Comp>
    );
  }
);
IconButton.displayName = "IconButton";

export { IconButton, iconButtonVariants };
